import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { HubShell } from '../../components/hub/shell';
import { fmtNumber } from '../../components/shared/format';
import ClientRow from '../../components/hub/ClientRow';
import ErrorCard from '../../components/hub/ErrorCard';
import ClientsLegend from '../../components/hub/ClientsLegend';
import { useNewClients } from '../../components/hub/useNewClients';
import { useOwnerSuggestions } from '../../components/hub/useOwnerSuggestions';
import { canAssignAccountOwner, canSuggestOwnersInList } from '../../components/hub/client/ownerCapability';
import { useTileData } from '../../hooks/useTileData';
import { useActiveRole } from '../../hooks/useActiveRole';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useHubAccess } from './access';
import {
  getClientAttention,
  getClientEngagement,
  getClientsWithoutOwner,
  getMyClients,
} from '../../api/hub';

// ── Row field readers ────────────────────────────────────────────────────────
// v_client_attention keys the client under `client_id` / `client`;
// v_client_engagement carries the churn signal under the same `client_id`.
// (Confirmed live by 13_CLIENT_PROBE — no guessed fallbacks.)
const idOf = (r) => r?.client_id ?? null;
const nameOf = (r) => r?.client ?? '—';
const num = (v) => (v == null || Number.isNaN(Number(v)) ? 0 : Number(v));

// Whether an attention row makes its client "flagged": an active signal — something leaking
// or pending — not merely being on the books. Open work alone is normal and does not qualify.
const isFlagged = (a) =>
  num(a?.broken_promises) > 0 ||
  num(a?.overdue) > 0 ||
  num(a?.waiting_on_them) > 0 ||
  num(a?.needs_help) > 0;

/** The ids of the flagged clients in a v_client_attention result. */
const flaggedIdsOf = (list) => new Set((list || []).filter(isFlagged).map(idOf).filter((v) => v != null));

const PAGE_SIZE = 25;
// A contact who used to ask for things and has been silent this long reads as
// "gone quiet" for the insight summary — the same threshold the churn view leans on.
const QUIET_DAYS = 90;

// ── Consequence ordering (spec §2) ───────────────────────────────────────────
// "Sort by consequence, not alphabetically. A client with four broken promises
// and no contact in six weeks belongs at the top." The composite weights broken
// promises above overdue above quiet time, so any broken promise outranks any
// amount of merely-overdue work, and quiet days only break ties among equals.
// days_quiet is capped so a single very stale client can't dominate purely on age.
const severityOf = (r) =>
  num(r.broken_promises) * 1_000_000 +
  num(r.overdue) * 1000 +
  Math.min(num(r.days_quiet), 999);

// "gone quiet" view (spec §2): clients who used to ask for things and stopped —
// days_since_any_request descending. Invisible if you only count open work.
const goneQuietOf = (r) => num(r.days_since_any_request);

// The views the list offers. Presented as underline tabs styled after the Now
// page — the same feed re-renders per view, so these are toggle buttons
// (aria-pressed), not a tablist with per-view tabpanels.
//
// Three cuts that PARTITION the roster — every client lands in exactly one.
// Needs Attention takes precedence: a client that is both a signal and quiet shows
// only there (the sharper cut), so the tabs never double-count and their counts
// sum to the roster total. "Gone Quiet" is therefore the quiet clients that AREN'T
// already flagged; "Steady" is the rest — nothing flagged, still in contact.
//
// There is deliberately NO "No owner" VIEW: account ownership isn't rolled out
// yet, so ~all clients are unowned — a filter selecting nearly the whole list
// narrows nothing. Ownership still rides each row as an "Unassigned" label (the
// same convention as the Now / Work rows), not as its own cut.
const VIEWS = [
  { key: 'attention', label: 'Needs Attention' },
  { key: 'quiet', label: 'Gone Quiet' },
  { key: 'steady', label: 'Steady' },
];

// The one "insight" surface above the feed — the Now feed's takeaway: a white bar with an
// amber rail and square, the lead of the sentence in bold and the rest in regular weight.
// The text is exactly the composed sentence; only its lead is set bold.
function InsightCard({ says, onDismiss }) {
  // "<lead> — <tail>": bold the lead, keep the rest. A sentence with no " — " is all lead.
  const cut = says.indexOf(' — ');
  const lead = cut === -1 ? says : says.slice(0, cut);
  const tail = cut === -1 ? '' : says.slice(cut);
  return (
    <div
      role="region"
      aria-label="Clients insight"
      className="relative mt-4 flex items-start gap-3 rounded-lg border border-l-4 border-gray-200 border-l-amber-400 bg-white py-3 pl-4 pr-10 dark:border-gray-800 dark:border-l-amber-500 dark:bg-gray-900"
    >
      <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm bg-amber-500" aria-hidden="true" />
      <p className="min-w-0 text-[15px] leading-relaxed text-gray-700 dark:text-gray-300">
        <strong className="font-semibold text-gray-900 dark:text-gray-100">{lead}</strong>
        {tail}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss insight"
        className="absolute right-2 top-2 p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

/**
 * The finding the banner leads with when any client has broken promises: the worst
 * client by the list's own consequence order and its share of every broken promise on
 * the page. Null when nobody has one or the worst client has no usable name.
 * @param {object[]} rows  The page's list rows (name, brokenPromises, _severity).
 * @returns {string|null}  "<lead>" only; the caller appends the nudge.
 */
function brokenPromiseFinding(rows) {
  const withBroken = rows.filter((r) => r.brokenPromises > 0);
  if (!withBroken.length) return null;
  const [top] = [...withBroken].sort(
    (a, b) => b._severity - a._severity || String(a.name).localeCompare(String(b.name)),
  );
  if (!top.name || top.name === '—') return null;

  const noun = (n) => (n === 1 ? 'broken promise' : 'broken promises');
  const total = withBroken.reduce((sum, r) => sum + r.brokenPromises, 0);
  if (withBroken.length === 1) {
    return `${top.name} has ${fmtNumber(top.brokenPromises)} ${noun(top.brokenPromises)}`;
  }
  return `${top.name} accounts for ${fmtNumber(top.brokenPromises)} of the ${fmtNumber(total)} ${noun(total)} across ${fmtNumber(withBroken.length)} clients`;
}

// A finding sentence composed from the loaded rows — never fabricated, and null
// when there is nothing worth flagging (so the card is absent, not empty).
// With broken promises it names the worst client and its share, so the banner says
// where to start rather than restating the tab counts. With none, it falls back to the
// counts that mirror the tabs: "gone quiet" matches the Gone Quiet tab — silent past
// the threshold AND not already flagged — so the two agree. No-owner is intentionally
// NOT summarised here: with ~all clients unowned it is noise, not a finding (the same
// reason there is no "No owner" view).
function composeInsight(rows) {
  if (!rows.length) return null;
  const attention = rows.filter((r) => r._needsAttention).length;
  const quiet = rows.filter(
    (r) => !r._needsAttention && r.daysSinceAnyRequest >= QUIET_DAYS,
  ).length;

  const finding = brokenPromiseFinding(rows);
  if (finding) return `${finding} — clear the reds first.`;

  const parts = [];
  if (attention) parts.push(`${fmtNumber(attention)} ${attention === 1 ? 'client needs' : 'clients need'} attention`);
  if (quiet) parts.push(`${fmtNumber(quiet)} ${quiet === 1 ? 'has' : 'have'} gone quiet`);
  if (!parts.length) return null;
  return `${parts.join(' and ')}.`;
}

const HubClientsPage = () => {
  const navigate = useNavigate();
  const role = useActiveRole();
  const { loading: authLoading } = useAuth();
  // Workspace-based access (Epic 2 · S2.6): the wall lives in the database, not
  // in the legacy role number. A person who holds at least one ClientHub
  // workspace can open this page; a non-holder is sent to their own dashboard.
  const canHub = useHubAccess();
  const { loading: wsLoading } = useWorkspace();

  const [view, setView] = useState('attention'); // 'attention' | 'quiet' | 'steady'
  const [page, setPage] = useState(1);
  const [insightDismissed, setInsightDismissed] = useState(false);

  const roster = useTileData(getMyClients, []);
  const attention = useTileData(getClientAttention, []);
  const engagement = useTileData(getClientEngagement, []);
  const unowned = useTileData(getClientsWithoutOwner, []);

  // "New" = a client that has just become flagged. A background poll counts them for the
  // "↑ N new" pill without touching the list; the rows tint until opened or 10 minutes pass.
  const flaggedIds = useMemo(
    () => (attention.data ? flaggedIdsOf(attention.data.data) : null),
    [attention.data],
  );
  const fetchFlaggedIds = useCallback(async () => flaggedIdsOf((await getClientAttention())?.data), []);
  const fresh = useNewClients({ flaggedIds, fetchFlaggedIds });

  // Which clients have no account owner — powers the per-row "Unassigned" label
  // (the same ownership convention as the Now / Work rows).
  const unownedIds = useMemo(() => {
    const list = unowned.data?.data || [];
    return new Set(list.map(idOf).filter((v) => v != null));
  }, [unowned.data]);

  // The full roster is the base — every client appears (so "All clients" matches
  // the sidebar total); attention (findings) and engagement (churn) are joined in
  // by client id. A client with no attention row reads as a calm, no-signal row
  // rather than being dropped from the list entirely.
  const rows = useMemo(() => {
    const attById = new Map((attention.data?.data || []).map((a) => [idOf(a), a]));
    const engById = new Map((engagement.data?.data || []).map((e) => [idOf(e), e]));
    return (roster.data?.data || [])
      .map((c) => {
        const id = c.id ?? null;
        const a = attById.get(id) || {};
        const e = engById.get(id) || {};
        return {
          id,
          name: c.name || nameOf(a),
          openWork: num(a.open_work),
          waitingOnThem: num(a.waiting_on_them),
          needsHelp: num(a.needs_help),
          overdue: num(a.overdue),
          brokenPromises: num(a.broken_promises),
          // Preserve null (no last-contact record) distinct from 0 (contacted today)
          // so the row can render "Last contact unknown" vs "Last spoke today".
          daysQuiet: a.days_quiet == null ? null : num(a.days_quiet),
          // Preserve null (no request on record) distinct from 0 (asked today) for
          // the row's recency line; _goneQuiet below stays numeric for the filter.
          daysSinceAnyRequest: e.days_since_any_request == null ? null : num(e.days_since_any_request),
          requestsEver: num(e.requests_ever),
          _severity: severityOf(a),
          _goneQuiet: goneQuietOf(e),
          // "Needs attention" is a flagged client: a broken promise, overdue work, someone
          // waiting on us, or a help flag (see isFlagged).
          _needsAttention: isFlagged(a),
        };
      })
      .filter((r) => r.id != null)
      // The tab each client lands in, so the row can word its severity slot: the three
      // populations PARTITION the roster (attention > quiet > steady).
      .map((r) => ({
        ...r,
        population: r._needsAttention ? 'attention' : r._goneQuiet >= QUIET_DAYS ? 'quiet' : 'steady',
      }));
  }, [roster.data, attention.data, engagement.data]);

  // The three views PARTITION the roster — every client lands in exactly one, so
  // the counts sum to the total. Precedence is attention > quiet > steady: a client
  // that is both leaking and silent belongs under Needs Attention (the sharper
  // signal), never in two tabs at once. Needs attention: an active signal, by
  // consequence. Gone quiet: silent past the threshold AND not already flagged,
  // most-stale first — the churn signal you'd miss if you only counted open work.
  // Steady: the rest — no signal and not quiet — ordered by who is drifting closest
  // to quiet.
  const populations = useMemo(() => {
    const byQuietDesc = (list) => [...list].sort((a, b) => b._goneQuiet - a._goneQuiet);
    return {
      attention: [...rows.filter((r) => r._needsAttention)].sort((a, b) => b._severity - a._severity),
      quiet: byQuietDesc(rows.filter((r) => !r._needsAttention && r._goneQuiet >= QUIET_DAYS)),
      steady: byQuietDesc(rows.filter((r) => !r._needsAttention && r._goneQuiet < QUIET_DAYS)),
    };
  }, [rows]);

  const counts = {
    attention: populations.attention.length,
    quiet: populations.quiet.length,
    steady: populations.steady.length,
  };
  const visibleRows = populations[view] || [];

  const insight = useMemo(() => composeInsight(rows), [rows]);

  // Re-seat the pager whenever the view or the population changes, so a tab never
  // leaves you stranded on a now-empty page.
  useEffect(() => { setPage(1); }, [view, visibleRows.length]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = visibleRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // One batched suggestion read for the unassigned clients on THIS page only. Off until the
  // backend confirms the read (canSuggestOwnersInList); with it off no call is made.
  const ownerSuggestions = useOwnerSuggestions(
    pageRows.filter((r) => unownedIds.has(r.id)).map((r) => r.id),
    canSuggestOwnersInList(),
  );

  // Page-level gate: workspace-based. Wait for auth AND the workspace lens so a
  // workspace-holder is never bounced to the client dashboard during the resolve
  // window; a confirmed non-holder (no workspace) goes to their own dashboard.
  if (authLoading || wsLoading) {
    return (
      <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen" role="status" aria-label="Loading">
        <span className="sr-only">Loading…</span>
      </div>
    );
  }
  if (!canHub) {
    return <Navigate to={role.isClient ? '/client-dashboard' : '/dashboard'} replace />;
  }

  // The roster is the base list; attention + engagement enrich it. Wait for all
  // three before the first paint so findings don't blank-then-pop, and treat any
  // of the three failing as a load error (a partial list would mislead).
  const loading = roster.loading || attention.loading || engagement.loading;
  const loadError = roster.error || attention.error || engagement.error;
  const reload = () => {
    roster.refresh?.();
    attention.refresh?.();
    engagement.refresh?.();
    unowned.refresh?.();
  };
  const retry = reload;

  // The "↑ N new" pill: mark what the poll found as new, reload so the rows appear in their
  // normal, consequence-ordered places, and show the tab they land in.
  const showNew = () => {
    fresh.applyPending();
    setView('attention');
    reload();
  };

  return (
    <HubShell>
      <div className="px-4 md:px-6 lg:px-10 py-6">
        <header className="mb-5">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Clients</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Who needs attention — ordered by consequence, not alphabetically.
          </p>
        </header>

        {loading && (
          <div role="status" aria-label="Loading" className="mt-6 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-900"
              />
            ))}
            <span className="sr-only">Loading…</span>
          </div>
        )}

        {!loading && loadError && (
          <ErrorCard
            className="mt-6"
            message="The client list isn't loading right now. Please try again shortly."
            onRetry={retry}
          />
        )}

        {!loading && !loadError && (
          <>
            {insight && !insightDismissed && (
              <InsightCard says={insight} onDismiss={() => setInsightDismissed(true)} />
            )}

            {/* View tabs — Needs attention / Gone quiet / Steady, as the Now feed's
                segmented scope switch. Toggle buttons (aria-pressed), not a tablist: the
                same feed re-renders per view. The info icon sits right beside them — one
                explanation of the labels, rails and numbers for the whole list, rather than
                a repeated icon on every row. */}
            <div className="mt-4 flex items-center gap-2">
              <div
                className="inline-flex overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
                aria-label="Client views"
              >
                {VIEWS.map((v) => {
                  const active = view === v.key;
                  return (
                    <button
                      key={v.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setView(v.key)}
                      className={`px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                        active
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                      }`}
                    >
                      {v.label}
                      <span className={`ml-2 text-xs font-normal tabular-nums ${active ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'}`}>
                        {fmtNumber(counts[v.key])}
                      </span>
                    </button>
                  );
                })}
              </div>
              <ClientsLegend />
              {fresh.pendingCount > 0 && (
                <button
                  type="button"
                  data-testid="clients-new-pill"
                  onClick={showNew}
                  aria-label={`${fresh.pendingCount} newly flagged ${fresh.pendingCount === 1 ? 'client' : 'clients'} — show ${fresh.pendingCount === 1 ? 'it' : 'them'}`}
                  className="ml-auto inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-semibold tabular-nums text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                >
                  <span aria-hidden="true">↑</span> {fmtNumber(fresh.pendingCount)} new
                </button>
              )}
            </div>

            {visibleRows.length === 0 ? (
              <div className="mt-6 rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  No clients to show
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {view === 'quiet'
                    ? 'No clients have gone quiet.'
                    : view === 'steady'
                      ? 'No steady clients — every client needs attention or has gone quiet.'
                      : 'No clients need attention right now.'}
                </p>
              </div>
            ) : (
              <>
                {/* A composed situation feed — each client reads as a finding,
                    never a row of raw counts (spec §2/§7). */}
                <ul className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
                  {pageRows.map((r) => (
                    <ClientRow
                      key={r.id}
                      row={r}
                      unassigned={unownedIds.has(r.id)}
                      suggestion={ownerSuggestions.suggestions.get(r.id) || null}
                      canAccept={canAssignAccountOwner()}
                      onOwnerAssigned={() => { unowned.refresh?.(); ownerSuggestions.refresh?.(); }}
                      isNew={fresh.isNew(r.id)}
                      onOpen={() => {
                        // Opening a client makes it old.
                        fresh.markOpened(r.id);
                        navigate(`/hub/clients/${r.id}`);
                      }}
                    />
                  ))}
                </ul>

                {pageCount > 1 && (
                  <nav className="flex items-center justify-between gap-3 pt-4" aria-label="Pagination">
                    <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      {fmtNumber((currentPage - 1) * PAGE_SIZE + 1)}–{fmtNumber(Math.min(currentPage * PAGE_SIZE, visibleRows.length))} of {fmtNumber(visibleRows.length)}
                    </span>
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage <= 1}
                        className="px-3 py-1.5 rounded-lg border text-sm border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        Page {fmtNumber(currentPage)} of {fmtNumber(pageCount)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={currentPage >= pageCount}
                        className="px-3 py-1.5 rounded-lg border text-sm border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </nav>
                )}
              </>
            )}
          </>
        )}
      </div>
    </HubShell>
  );
};

export default HubClientsPage;
